/**
 * EmbedWidgets.jsx — Widgets embarquables pour intégration WordPress / site tiers.
 *
 * Routes : /embed/next-match · /embed/upcoming · /embed/calendar · /embed/standings · /embed/live · /embed/last-result
 *
 * Caractéristiques :
 * - Page minimaliste sans header/footer (le widget remplit toute la zone)
 * - CSS auto-suffisant (couleurs IPHB, fonts modernes)
 * - Auto-resize via postMessage('embed-resize', height) → le parent ajuste l'iframe
 * - Polling rafraîchissement toutes les 60 s
 * - Responsive mobile/tablette via CSS pur (pas de breakpoints Tailwind nécessaires)
 * - Param URL `?theme=dark|light` (défaut dark)
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";

const PRIMARY = "#4F1B7C";
const PINK = "#EC1F7C";
const BG_DARK = "#0a0414";

// Post height to parent window for iframe auto-resize
function useReportHeight() {
  return useCallback(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    const h = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
    );
    try {
      window.parent.postMessage({ type: "iphb-embed-resize", height: h }, "*");
    } catch { /* noop */ }
  }, []);
}

function useEmbedRefresh(callback, intervalMs = 60000) {
  useEffect(() => {
    callback();
    const id = setInterval(callback, intervalMs);
    return () => clearInterval(id);
  }, [callback, intervalMs]);
}

const fmtDateFr = (iso, time) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
    return time ? `${datePart} · ${time}` : datePart;
  } catch { return ""; }
};
const matchHomeTeam = (m) => m?.home_team || m?.home || m?.team_home || "";
const matchAwayTeam = (m) => m?.away_team || m?.away || m?.team_away || "";
const matchHomeLogo = (m) => m?.home_logo || m?.home_logo_url || "";
const matchAwayLogo = (m) => m?.away_logo || m?.away_logo_url || "";
const matchHomeScore = (m) => m?.score_home ?? m?.home_score ?? null;
const matchAwayScore = (m) => m?.score_away ?? m?.away_score ?? null;
const matchDateLine = (m) => fmtDateFr(m?.date_iso || m?.date || m?.start_at, m?.time);

// =========================================================
// EMBED LAYOUT (fond transparent par défaut pour intégration WP)
// =========================================================
export default function EmbedWidgets() {
  const { widget } = useParams();
  const [params] = useSearchParams();
  const theme = params.get("theme") || "transparent";
  const isDark = theme === "dark";
  const isLight = theme === "light";
  // Transparent par défaut : la page WP fournit le fond.
  const isTransparent = !isDark && !isLight;

  const reportHeight = useReportHeight();
  useEffect(() => {
    const ro = new ResizeObserver(() => reportHeight());
    ro.observe(document.body);
    // Rends le <html> et le <body> transparents pour l'iframe
    if (isTransparent && typeof document !== "undefined") {
      document.documentElement.style.background = "transparent";
      document.body.style.background = "transparent";
    }
    return () => ro.disconnect();
  }, [reportHeight, isTransparent]);

  // Texte par défaut adapté au fond (dark text si transparent, on suppose un fond clair WP)
  const textColor = isDark ? "#ffffff" : (isLight ? "#0a0414" : "inherit");
  const styles = {
    minHeight: "auto",
    background: isDark ? BG_DARK : (isLight ? "#ffffff" : "transparent"),
    color: textColor,
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    padding: "8px",
    boxSizing: "border-box",
  };

  let Widget = null;
  switch (widget) {
    case "next-match":    Widget = <WidgetNextMatch isDark={isDark} isTransparent={isTransparent}/>; break;
    case "last-result":   Widget = <WidgetLastResult isDark={isDark} isTransparent={isTransparent}/>; break;
    case "upcoming":      Widget = <WidgetUpcoming isDark={isDark} isTransparent={isTransparent}/>; break;
    case "calendar":      Widget = <WidgetCalendar isDark={isDark} isTransparent={isTransparent}/>; break;
    case "standings":     Widget = <WidgetStandings isDark={isDark} isTransparent={isTransparent}/>; break;
    case "live":          Widget = <WidgetLive isDark={isDark} isTransparent={isTransparent}/>; break;
    default:              Widget = <WidgetDirectory/>;
  }

  return (
    <div style={styles} data-testid={`embed-widget-${widget || "directory"}`}>
      {Widget}
      <EmbedStyles isDark={isDark} isTransparent={isTransparent}/>
    </div>
  );
}

function EmbedStyles({ isDark, isTransparent }) {
  // En mode transparent (intégration WP) : page transparente MAIS cartes blanches
  // pour que le contenu soit lisible quelle que soit la couleur de fond WP.
  const cardBg = isTransparent
    ? "#ffffff"
    : (isDark ? "rgba(255,255,255,0.04)" : "#f8f5fb");
  const cardBorder = isTransparent
    ? "rgba(127,30,116,0.12)"
    : (isDark ? "rgba(255,255,255,0.08)" : "#ede9f7");
  const cardShadow = isTransparent
    ? "0 6px 24px rgba(79,27,124,0.08), 0 2px 8px rgba(0,0,0,0.04)"
    : "none";
  const cardTextColor = isTransparent ? "#0a0414" : "inherit";
  const tableBorder = isTransparent
    ? "rgba(127,30,116,0.12)"
    : (isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0");
  const tableBorderLight = isTransparent
    ? "rgba(127,30,116,0.07)"
    : (isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9");
  // Force transparency au niveau html/body pour neutraliser le background global du site
  const forceBg = isTransparent ? "transparent !important" : (isDark ? `${BG_DARK} !important` : "#ffffff !important");
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; }
      html, body, #root { margin: 0; background: ${forceBg}; }
      .iphb-card {
        background: ${cardBg};
        border: 1px solid ${cardBorder};
        border-radius: 16px;
        padding: 16px;
        box-shadow: ${cardShadow};
        color: ${cardTextColor};
      }
      .iphb-section-bare {
        background: transparent;
        border: 0;
        padding: 0;
      }

      /* Calendrier complet — grille toujours 3 colonnes (fidèle au site
         de référence istreshandball.com). Sur écran très étroit (<420px),
         on passe à 1 colonne pour rester lisible. */
      .iphb-cal-grid {
        display: grid;
        gap: 16px;
        padding: 4px 4px 12px;
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      @media (max-width: 420px) {
        .iphb-cal-grid { grid-template-columns: 1fr; }
      }

      /* Confettis pour les matchs gagnés (widget calendar) */
      @keyframes iphb-confetti-fall {
        0%   { transform: translate3d(0, -10px, 0) rotate(0deg);   opacity: 0.9; }
        100% { transform: translate3d(0, 140px, 0) rotate(720deg); opacity: 0;   }
      }
      @media (prefers-reduced-motion: reduce) {
        [aria-hidden="true"] span[style*="iphb-confetti-fall"] { animation: none !important; opacity: 0 !important; }
      }
      .iphb-pill { display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:999px;
        font-size:9px; font-weight:900; letter-spacing:0.18em; text-transform:uppercase;
        background: ${PINK}26; color: ${PINK}; }
      .iphb-title { font-weight:900; text-transform:uppercase; letter-spacing:0.06em; }
      .iphb-btn-primary { display:inline-block; padding:8px 16px; border-radius:999px;
        background: linear-gradient(135deg, ${PRIMARY}, ${PINK});
        color: white; font-weight:900; font-size:11px; letter-spacing:0.1em; text-transform:uppercase;
        text-decoration:none; }
      .iphb-loader { text-align:center; padding:24px 10px; opacity:0.6; font-size:12px; font-style:italic; }
      .iphb-empty { text-align:center; padding:24px 10px; opacity:0.5; font-size:12px; font-style:italic; }

      /* === Logos team === */
      .iphb-team-logo {
        width: clamp(90px, 18vw, 140px);
        height: clamp(90px, 18vw, 140px);
        object-fit: contain;
        filter: drop-shadow(0 4px 16px rgba(0,0,0,0.18));
      }
      .iphb-team-logo-sm { width: 32px; height: 32px; object-fit: contain; }
      .iphb-logo-placeholder {
        display:flex; align-items:center; justify-content:center;
        background: linear-gradient(135deg, ${PRIMARY}30, ${PINK}30);
        border-radius: 14px;
        font-weight: 900; color: ${PINK};
      }

      /* === VS bloc central === */
      .iphb-vs {
        font-family:'Bebas Neue', Impact, sans-serif;
        font-weight: 900;
        font-size: clamp(36px, 7vw, 56px);
        background: linear-gradient(135deg, ${PRIMARY}, ${PINK});
        -webkit-background-clip: text; background-clip: text;
        -webkit-text-fill-color: transparent;
        letter-spacing: 0.04em; line-height: 1;
      }
      .iphb-score {
        font-family:'Bebas Neue', Impact, sans-serif;
        font-size: clamp(42px, 8vw, 64px);
        font-weight: 900;
        letter-spacing: 0.05em;
        line-height: 1;
        color: ${PINK};
        text-shadow: 0 2px 12px rgba(236,31,124,0.35);
      }
      .iphb-team-name {
        font-weight: 900;
        font-size: clamp(13px, 2vw, 16px);
        letter-spacing: 0.04em;
        text-transform: uppercase;
        line-height: 1.15;
        margin-top: 6px;
        max-width: 160px;
        text-align: center;
      }
      .iphb-meta-pill {
        display:inline-flex; align-items:center; gap:6px;
        background: ${PINK}22;
        color: ${PINK};
        padding: 4px 12px;
        border-radius: 999px;
        font-weight: 900;
        font-size: 11px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }

      /* === Standings === */
      .iphb-table { width: 100%; border-collapse: collapse; }
      .iphb-table th { padding: 9px 6px; text-align: left; font-size: 9px; text-transform: uppercase;
        letter-spacing: 0.1em; opacity: 0.55; font-weight: 800; border-bottom: 1px solid ${tableBorder}; }
      .iphb-table td { padding: 9px 6px; font-size: 12px; border-bottom: 1px solid ${tableBorderLight}; }
      .iphb-table tr.iphb-row-club td { background: ${PINK}18; font-weight: 700; }
      .iphb-table tr.iphb-row-club td:first-child { border-left: 3px solid ${PINK}; }

      /* === Carrousel autoscroll === */
      .iphb-marquee {
        position: relative;
        overflow: hidden;
        mask-image: linear-gradient(to right, transparent, black 4%, black 96%, transparent);
        -webkit-mask-image: linear-gradient(to right, transparent, black 4%, black 96%, transparent);
      }
      .iphb-marquee-track {
        display: flex;
        gap: 14px;
        width: max-content;
        animation: iphbMarquee 40s linear infinite;
      }
      .iphb-marquee:hover .iphb-marquee-track { animation-play-state: paused; }
      @keyframes iphbMarquee {
        from { transform: translateX(0); }
        to   { transform: translateX(-50%); }
      }
      .iphb-match-card {
        flex: 0 0 auto;
        width: clamp(240px, 32vw, 300px);
      }

      .iphb-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .iphb-live-pulse {
        width:8px; height:8px; border-radius:50%; background:#ef4444; display:inline-block;
        box-shadow: 0 0 0 0 #ef4444; animation: iphbPulse 1.5s infinite;
      }
      @keyframes iphbPulse {
        0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
        70% { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
        100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
      }

      @media (max-width: 480px) { .iphb-hide-mobile { display: none; } }
    `}</style>
  );
}

// =========================================================
// WIDGET: NEXT MATCH (gros logo VS gros logo, sans titre)
// =========================================================
function WidgetNextMatch({ isDark, isTransparent }) {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sport/next-match");
      setMatch(data?.match || data || null);
    } catch { setMatch(null); }
    finally { setLoading(false); }
  }, []);
  useEmbedRefresh(load);

  if (loading) return <div className="iphb-loader">Chargement…</div>;
  if (!match) return <div className="iphb-empty">Aucun match programmé</div>;

  return (
    <div className="iphb-section-bare" data-testid="embed-next-match">
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "clamp(8px,2vw,28px)", padding: "12px 4px" }}>
        <TeamBlock team={matchHomeTeam(match)} logo={matchHomeLogo(match)} side="home"/>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <span className="iphb-vs">VS</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
            <span className="iphb-meta-pill">{matchDateLine(match)}</span>
            {match.venue && <span style={{ fontSize: 11, opacity: 0.65, fontWeight: 600 }}>📍 {match.venue}</span>}
          </div>
        </div>
        <TeamBlock team={matchAwayTeam(match)} logo={matchAwayLogo(match)} side="away"/>
      </div>
    </div>
  );
}

function TeamBlock({ team, logo, side }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      {logo ? (
        <img src={logo} alt={team || ""} className="iphb-team-logo"/>
      ) : (
        <div className="iphb-team-logo iphb-logo-placeholder" style={{ fontSize: 36 }}>
          {(team || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="iphb-team-name">{team || "À confirmer"}</div>
    </div>
  );
}

// =========================================================
// WIDGET: LAST RESULT (gros logo SCORE gros logo, sans titre)
// =========================================================
function WidgetLastResult({ isDark, isTransparent }) {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sport/last-result");
      setMatch(data?.match || data || null);
    } catch { setMatch(null); }
    finally { setLoading(false); }
  }, []);
  useEmbedRefresh(load);

  if (loading) return <div className="iphb-loader">Chargement…</div>;
  if (!match) return <div className="iphb-empty">Aucun résultat récent</div>;

  const homeScore = matchHomeScore(match);
  const awayScore = matchAwayScore(match);
  return (
    <div className="iphb-section-bare" data-testid="embed-last-result">
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "clamp(8px,2vw,28px)", padding: "12px 4px" }}>
        <TeamBlock team={matchHomeTeam(match)} logo={matchHomeLogo(match)} side="home"/>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
          <div className="iphb-score">{homeScore ?? "-"} <span style={{ opacity: 0.4, margin: "0 6px" }}>-</span> {awayScore ?? "-"}</div>
          <span className="iphb-meta-pill" style={{ background: "rgba(127,30,116,0.18)", color: PRIMARY }}>{matchDateLine(match)}</span>
        </div>
        <TeamBlock team={matchAwayTeam(match)} logo={matchAwayLogo(match)} side="away"/>
      </div>
    </div>
  );
}

// =========================================================
// WIDGET: UPCOMING (autoscroll JS + flèches navigation manuelle)
// =========================================================
function WidgetUpcoming({ isDark, isTransparent }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sport/upcoming-matches");
      const arr = data?.matches || data?.items || data || [];
      setMatches(Array.isArray(arr) ? arr : []);
    } catch { setMatches([]); }
    finally { setLoading(false); }
  }, []);
  useEmbedRefresh(load);

  // Boucle autoscroll fluide (rAF) — pause au hover ou si flèche cliquée
  useEffect(() => {
    if (!matches.length) return;
    let raf;
    const speed = 0.45; // px / frame ≈ 27 px/s
    const tick = () => {
      const t = trackRef.current;
      if (t && !pausedRef.current) {
        offsetRef.current += speed;
        const half = t.scrollWidth / 2;
        if (offsetRef.current >= half) offsetRef.current -= half;
        t.style.transform = `translateX(-${offsetRef.current}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [matches]);

  const move = (dir) => {
    const t = trackRef.current;
    if (!t || !t.firstChild) return;
    pausedRef.current = true;
    const cardW = t.firstChild.getBoundingClientRect().width + 14; // +gap
    let newOffset = offsetRef.current + dir * cardW;
    const half = t.scrollWidth / 2;
    if (newOffset < 0) newOffset += half;
    if (newOffset >= half) newOffset -= half;
    offsetRef.current = newOffset;
    t.style.transition = "transform 380ms cubic-bezier(0.4, 0, 0.2, 1)";
    t.style.transform = `translateX(-${newOffset}px)`;
    setTimeout(() => {
      if (t) t.style.transition = "";
      pausedRef.current = false;
    }, 420);
  };

  if (loading) return <div className="iphb-loader">Chargement du calendrier…</div>;
  if (!matches.length) return <div className="iphb-empty">Aucun match à venir</div>;

  const items = matches.slice(0, 10);
  const doubled = [...items, ...items];

  return (
    <div data-testid="embed-upcoming" style={{ position: "relative" }}>
      {/* Flèche gauche */}
      <button
        type="button"
        onClick={() => move(-1)}
        aria-label="Match précédent"
        data-testid="upcoming-prev"
        style={arrowBtnStyle("left")}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      {/* Flèche droite */}
      <button
        type="button"
        onClick={() => move(1)}
        aria-label="Match suivant"
        data-testid="upcoming-next"
        style={arrowBtnStyle("right")}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>

      <div
        className="iphb-marquee"
        style={{ padding: "4px 36px", overflow: "hidden", background: "transparent" }}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <div
          ref={trackRef}
          style={{ display: "flex", gap: 14, width: "max-content", willChange: "transform" }}
        >
          {doubled.map((m, i) => (
            <div key={`${m.id || i}-${i}`} className="iphb-card iphb-match-card">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 10, opacity: 0.55, fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {matchDateLine(m)}
                </span>
                {m.matchday && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 999, background: `${PINK}22`, color: PINK, fontWeight: 900 }}>{/^J/i.test(String(m.matchday)) ? m.matchday : `J${m.matchday}`}</span>}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }}>
                <TeamMini team={matchHomeTeam(m)} logo={matchHomeLogo(m)}/>
                <span style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 22, fontWeight: 900, color: PINK }}>VS</span>
                <TeamMini team={matchAwayTeam(m)} logo={matchAwayLogo(m)}/>
              </div>
              {m.venue && <div style={{ fontSize: 10, opacity: 0.55, marginTop: 8, textAlign: "center" }}>📍 {m.venue}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function arrowBtnStyle(side) {
  return {
    position: "absolute",
    top: "50%",
    [side]: 0,
    transform: "translateY(-50%)",
    zIndex: 5,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${PRIMARY}, ${PINK})`,
    color: "#ffffff",
    boxShadow: "0 4px 16px rgba(79,27,124,0.35)",
    transition: "transform 200ms ease, box-shadow 200ms ease",
  };
}

function TeamMini({ team, logo }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
      {logo ? (
        <img src={logo} alt="" style={{ width: 52, height: 52, objectFit: "contain", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.15))" }}/>
      ) : (
        <div style={{ width: 52, height: 52, borderRadius: 12, background: `linear-gradient(135deg, ${PRIMARY}30, ${PINK}30)`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: PINK, fontSize: 18 }}>
          {(team || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 800, lineHeight: 1.15, textAlign: "center", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{team || "—"}</div>
    </div>
  );
}

// =========================================================
// WIDGET: CALENDAR (calendrier complet de la saison)
// - Affiche tous les matchs (passés + à venir)
// - Matchs passés : score affiché + confettis si Istres a gagné
// - Auto-scroll au prochain match à venir au chargement
// =========================================================
function WidgetCalendar({ isDark, isTransparent }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ourCode, setOurCode] = useState("ist");
  const listRef = useRef(null);
  const nextMatchRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sport/team-calendar");
      const arr = data?.matches || data?.items || data || [];
      setMatches(Array.isArray(arr) ? arr : []);
      // Détecte le code de l'équipe (ist pour Istres) à partir des données
      const sample = Array.isArray(arr) ? arr.find(m => /istres/i.test(m.home_team || "") || /istres/i.test(m.away_team || "")) : null;
      if (sample) {
        if (/istres/i.test(sample.home_team || "")) setOurCode((sample.home_code || "ist").toLowerCase());
        else if (/istres/i.test(sample.away_team || "")) setOurCode((sample.away_code || "ist").toLowerCase());
      }
    } catch { setMatches([]); }
    finally { setLoading(false); }
  }, []);
  useEmbedRefresh(load, 120000);

  // Auto-scroll au prochain match (1er à venir) à l'ouverture
  useEffect(() => {
    if (!matches.length || loading) return;
    const idx = matches.findIndex(m => isUpcoming(m));
    if (idx === -1) return;
    // Petit delay pour laisser le rendu se faire
    const t = setTimeout(() => {
      const el = nextMatchRef.current;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [matches, loading]);

  if (loading) return <div className="iphb-loader">Chargement du calendrier…</div>;
  if (!matches.length) return <div className="iphb-empty">Aucun match planifié</div>;

  // Index du 1er upcoming pour le marquage du ref
  const nextIdx = matches.findIndex(m => isUpcoming(m));

  return (
    <div data-testid="embed-calendar" ref={listRef} className="iphb-cal-grid">
      {matches.map((m, i) => (
        <MatchCalendarCard
          key={m.id || i}
          ref={i === nextIdx ? nextMatchRef : null}
          match={m}
          ourCode={ourCode}
          isNext={i === nextIdx}
          isTransparent={isTransparent}
        />
      ))}
    </div>
  );
}

// Match terminé : a un score non-nul ou est dans le passé avec score connu
function getMatchOutcome(m, ourCode) {
  const sh = Number(m.score_home || 0);
  const sa = Number(m.score_away || 0);
  if (sh === 0 && sa === 0) return null; // pas de score = à venir
  const ourHome = (m.home_code || "").toLowerCase() === ourCode;
  const ourScore = ourHome ? sh : sa;
  const oppScore = ourHome ? sa : sh;
  if (ourScore > oppScore) return "won";
  if (ourScore < oppScore) return "lost";
  return "draw";
}
function isUpcoming(m) {
  const sh = Number(m.score_home || 0);
  const sa = Number(m.score_away || 0);
  return sh === 0 && sa === 0;
}

const MatchCalendarCard = React.forwardRef(function MatchCalendarCard({ match, ourCode, isNext, isTransparent }, ref) {
  const outcome = getMatchOutcome(match, ourCode);
  const upcoming = !outcome;
  const home = matchHomeTeam(match);
  const away = matchAwayTeam(match);
  const hLogo = matchHomeLogo(match);
  const aLogo = matchAwayLogo(match);
  const sh = match.score_home;
  const sa = match.score_away;

  // Couleur du badge état
  const stateMeta = upcoming
    ? { label: "À venir", color: PINK, bg: `${PINK}1A` }
    : outcome === "won"
      ? { label: "Victoire", color: "#10b981", bg: "#10b9811A" }
      : outcome === "lost"
        ? { label: "Défaite", color: "#ef4444", bg: "#ef44441A" }
        : { label: "Nul", color: "#64748b", bg: "#64748b1A" };

  return (
    <div
      ref={ref}
      data-testid={`calendar-match-${match.id || match.date_iso}`}
      data-state={upcoming ? "upcoming" : outcome}
      className="iphb-card"
      style={{
        position: "relative",
        padding: 12,
        border: isNext ? `2px solid ${PINK}` : undefined,
        boxShadow: isNext
          ? `0 8px 28px ${PINK}26, 0 0 0 3px ${PINK}1A`
          : undefined,
        overflow: "hidden",
      }}
    >
      {/* Confettis pour les victoires */}
      {outcome === "won" && <Confetti />}

      {/* Badge "PROCHAIN MATCH" si c'est celui-ci */}
      {isNext && (
        <div style={{
          position: "absolute", top: -1, right: 12,
          background: PINK, color: "#fff", fontSize: 9, fontWeight: 900,
          padding: "2px 10px", borderRadius: "0 0 8px 8px", letterSpacing: "0.18em", textTransform: "uppercase",
          boxShadow: `0 4px 12px ${PINK}66`,
        }}>
          ★ Prochain match
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        <span style={{ fontSize: 10, opacity: 0.6, fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {matchDateLine(match)}
        </span>
        <span style={{
          fontSize: 9, padding: "2px 8px", borderRadius: 999,
          background: stateMeta.bg, color: stateMeta.color, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          {stateMeta.label}
        </span>
        {match.matchday && (
          <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 999, background: `${PRIMARY}15`, color: PRIMARY, fontWeight: 900 }}>
            {/^J/i.test(String(match.matchday)) ? match.matchday : `J${match.matchday}`}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 10 }}>
        <TeamCalRow team={home} logo={hLogo} align="right" highlight={(match.home_code || "").toLowerCase() === ourCode}/>
        <ScoreOrVs upcoming={upcoming} sh={sh} sa={sa}/>
        <TeamCalRow team={away} logo={aLogo} align="left" highlight={(match.away_code || "").toLowerCase() === ourCode}/>
      </div>

      {match.venue && (
        <div style={{ fontSize: 10, opacity: 0.6, marginTop: 8, textAlign: "center" }}>
          📍 {match.venue}
        </div>
      )}
    </div>
  );
});

function TeamCalRow({ team, logo, align, highlight }) {
  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, justifyContent: align === "right" ? "flex-end" : "flex-start", minWidth: 0 }}>
      {align === "right" && (
        <span style={{ fontSize: 12, fontWeight: highlight ? 900 : 700, textAlign: "right", lineHeight: 1.15, color: highlight ? PRIMARY : "inherit" }}>{team || "—"}</span>
      )}
      {logo ? (
        <img src={logo} alt="" style={{ width: 38, height: 38, objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.12))", flexShrink: 0 }}/>
      ) : (
        <div style={{ width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${PRIMARY}30, ${PINK}30)`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, color: PINK, fontSize: 14, flexShrink: 0 }}>
          {(team || "?").slice(0, 1).toUpperCase()}
        </div>
      )}
      {align === "left" && (
        <span style={{ fontSize: 12, fontWeight: highlight ? 900 : 700, textAlign: "left", lineHeight: 1.15, color: highlight ? PRIMARY : "inherit" }}>{team || "—"}</span>
      )}
    </div>
  );
}

function ScoreOrVs({ upcoming, sh, sa }) {
  if (upcoming) {
    return (
      <span style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 24, fontWeight: 900, color: PINK, padding: "0 6px" }}>VS</span>
    );
  }
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 10, background: `${PRIMARY}12`, color: PRIMARY,
      fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 22, fontWeight: 900, letterSpacing: "0.05em",
    }}>
      <span>{sh}</span>
      <span style={{ opacity: 0.45 }}>:</span>
      <span>{sa}</span>
    </div>
  );
}

// Confettis CSS-only — particules animées qui tombent au top de la carte
function Confetti() {
  const PIECES = 14;
  const colors = ["#EC1F7C", "#FACC15", "#10B981", "#3B82F6", "#8B5CF6", "#F97316"];
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", borderRadius: 16,
      }}
    >
      {Array.from({ length: PIECES }).map((_, i) => {
        const left = (i / PIECES) * 100 + Math.random() * 6;
        const delay = -Math.random() * 4;
        const dur = 3 + Math.random() * 2.5;
        const size = 5 + Math.random() * 5;
        const color = colors[i % colors.length];
        const rotate = Math.random() * 360;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              top: -10,
              left: `${left}%`,
              width: size,
              height: size * 0.45,
              background: color,
              borderRadius: 1,
              transform: `rotate(${rotate}deg)`,
              animation: `iphb-confetti-fall ${dur}s linear ${delay}s infinite`,
              opacity: 0.85,
            }}
          />
        );
      })}
    </div>
  );
}



// =========================================================
// WIDGET: STANDINGS (classement)
// =========================================================
function WidgetStandings({ isDark }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clubSlug, setClubSlug] = useState("");

  const load = useCallback(async () => {
    try {
      const [stdRes, infoRes] = await Promise.all([
        api.get("/sport/standings"),
        api.get("/sport/team-info").catch(() => ({ data: {} })),
      ]);
      const arr = stdRes.data?.standings || stdRes.data?.rows || stdRes.data || [];
      setRows(Array.isArray(arr) ? arr : []);
      setClubSlug((infoRes.data?.info?.team_code || infoRes.data?.team_code || infoRes.data?.info?.slug || "ist").toLowerCase());
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);
  useEmbedRefresh(load);

  if (loading) return <div className="iphb-loader">Chargement du classement…</div>;
  if (!rows.length) return <div className="iphb-empty">Classement indisponible</div>;

  const isClubRow = (r) => {
    const name = (r.team_name || r.team || r.name || "").toLowerCase();
    const code = (r.team_code || r.code || "").toLowerCase();
    if (clubSlug && (code === clubSlug || name.includes(clubSlug))) return true;
    return name.includes("istres");
  };

  return (
    <div data-testid="embed-standings">
      <div className="iphb-card" style={{ padding: 4, overflow: "hidden" }}>
        <table className="iphb-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}>#</th>
              <th>Équipe</th>
              <th style={{ textAlign: "center", width: 36 }}>J</th>
              <th style={{ textAlign: "center", width: 36 }}>Pts</th>
              <th style={{ textAlign: "center", width: 42, display: "table-cell" }} className="iphb-hide-mobile">+/-</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || r.team_code || i} className={isClubRow(r) ? "iphb-row-club" : ""}>
                <td style={{ fontWeight: 900, opacity: 0.6 }}>{r.rank ?? r.position ?? (i + 1)}</td>
                <td className="iphb-truncate" style={{ maxWidth: 180 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {r.logo_url && <img src={r.logo_url} alt="" className="iphb-team-logo iphb-team-logo-sm" style={{ width: 20, height: 20, padding: 2 }}/>}
                    <span>{r.team_name || r.team || r.name}</span>
                  </div>
                </td>
                <td style={{ textAlign: "center", fontWeight: 700 }}>{r.played ?? r.matches_played ?? "-"}</td>
                <td style={{ textAlign: "center", fontWeight: 900, color: PINK }}>{r.points ?? r.pts ?? "-"}</td>
                <td style={{ textAlign: "center", opacity: 0.7 }} className="iphb-hide-mobile">
                  {(r.goal_diff ?? r.diff ?? ((r.goals_for ?? 0) - (r.goals_against ?? 0))) || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`
        @media (max-width: 480px) { .iphb-hide-mobile { display: none; } }
      `}</style>
    </div>
  );
}

// =========================================================
// WIDGET: LIVE (gros logo + score live ou prochain match)
// =========================================================
function WidgetLive({ isDark, isTransparent }) {
  const [liveSession, setLiveSession] = useState(null);
  const [nextMatch, setNextMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [liveR, nextR] = await Promise.all([
        api.get("/match-live/active").catch(() => ({ data: null })),
        api.get("/sport/next-match").catch(() => ({ data: null })),
      ]);
      setLiveSession(liveR.data?.session || liveR.data || null);
      setNextMatch(nextR.data?.match || nextR.data || null);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <div className="iphb-loader">Chargement…</div>;

  if (liveSession && liveSession.id) {
    const s = liveSession;
    return (
      <div className="iphb-section-bare" data-testid="embed-live-active">
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
          <span className="iphb-meta-pill" style={{ background: "rgba(239,68,68,0.18)", color: "#ef4444" }}>
            <span className="iphb-live-pulse"/> En direct {s.minute ? `· ${s.minute}'` : (s.halftime ? "· Mi-temps" : "")}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "clamp(8px,2vw,28px)", padding: "8px 4px" }}>
          <TeamBlock team={s.home_team} logo={s.home_logo_url} side="home"/>
          <div className="iphb-score">{s.home_score ?? 0} <span style={{ opacity: 0.4, margin: "0 6px" }}>-</span> {s.away_score ?? 0}</div>
          <TeamBlock team={s.away_team} logo={s.away_logo_url} side="away"/>
        </div>
      </div>
    );
  }
  if (nextMatch) {
    return <WidgetNextMatch isDark={isDark} isTransparent={isTransparent}/>;
  }
  return <div className="iphb-empty">Aucun match en cours ni programmé</div>;
}

// =========================================================
// WIDGET: DIRECTORY (page d'accueil /embed sans param)
// =========================================================
function WidgetDirectory() {
  const widgets = [
    { id: "next-match",  label: "Prochain match",     desc: "Card unique du match à venir" },
    { id: "last-result", label: "Dernier résultat",   desc: "Score du dernier match disputé" },
    { id: "upcoming",    label: "Carrousel matchs",   desc: "5-8 prochains matchs en carrousel" },
    { id: "calendar",    label: "Calendrier saison",  desc: "Tous les matchs de la saison + scores + confettis sur victoires" },
    { id: "standings",   label: "Classement",         desc: "Classement complet LNH StarLigue" },
    { id: "live",        label: "Live / Prochain",    desc: "Match en cours OU prochain match" },
  ];
  return (
    <div data-testid="embed-directory">
      <h2 className="iphb-title" style={{ marginTop: 0 }}>Widgets IPHB — intégration WordPress</h2>
      <p style={{ fontSize: 12, opacity: 0.7 }}>
        Choisissez un widget ci-dessous. Chacun s'embarque via une iframe responsive sur votre site WP :
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        {widgets.map((w) => (
          <a key={w.id} href={`/embed/${w.id}`} className="iphb-card" style={{ display: "block", textDecoration: "none", color: "inherit" }}>
            <div style={{ fontWeight: 900, color: PINK }}>{w.label}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{w.desc}</div>
            <code style={{ fontSize: 11, opacity: 0.5, fontFamily: "monospace" }}>/embed/{w.id}</code>
          </a>
        ))}
      </div>
    </div>
  );
}
